import {useEffect, useRef} from 'react'

export default function OtpInput({value, onChange, length = 6, autoFocus = false, className = ''}) {
    const refs = useRef([])

    useEffect(() => {
        if (autoFocus) setTimeout(() => refs.current[0]?.focus(), 100)
    }, [autoFocus])

    // Mirror of `value` that is current DURING an event, before the parent re-renders.
    // The focus guard runs synchronously off a change, when the `value` prop is still
    // pre-keystroke — reading it there would bounce focus back to the box just filled.
    const nowRef = useRef(value)
    useEffect(() => {
        nowRef.current = value
    }, [value])

    const handleFocus = (idx) => {
        const now = nowRef.current
        const firstEmpty = now.findIndex((d) => d === '')
        const target = firstEmpty === -1 ? now.length - 1 : firstEmpty
        if (idx > target) refs.current[target]?.focus()
    }

    const handleChange = (raw, idx) => {
        if (!/^[0-9]?$/.test(raw)) return
        const next = [...value]
        next[idx] = raw
        nowRef.current = next
        onChange(next)
        // Focus SYNCHRONOUSLY, inside the input event. iOS Safari only keeps the
        // on-screen keyboard up when the next focus happens within the user gesture —
        // deferred through a timeout it closed after every digit.
        if (raw && refs.current[idx + 1]) {
            refs.current[idx + 1].focus()
        }
    }

    const handleKeyDown = (e, idx) => {
        if (e.key === 'Backspace') {
            if (!value[idx] && idx > 0) {
                const next = [...value]
                next[idx - 1] = ''
                onChange(next)
                refs.current[idx - 1].focus()
            } else {
                const next = [...value]
                next[idx] = ''
                onChange(next)
            }
            e.preventDefault()
            return
        }
        if (e.key === 'ArrowLeft' && idx > 0) refs.current[idx - 1].focus()
        if (e.key === 'ArrowRight' && idx < value.length - 1) refs.current[idx + 1].focus()
    }

    const handlePaste = (e, idx) => {
        e.preventDefault()
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '')
        if (!pasted) return
        const digits = pasted.split('')
        const next = [...value]
        for (let i = 0; i < digits.length && idx + i < value.length; i++) {
            next[idx + i] = digits[i]
        }
        nowRef.current = next
        onChange(next)
        const lastIndex = Math.min(idx + digits.length - 1, value.length - 1)
        refs.current[lastIndex]?.focus()
    }

    return (
        // Grid, not a fixed-width row: six 48px boxes plus gaps overflowed a narrow
        // phone and the first/last were clipped.
        <div className={`grid grid-cols-6 gap-2 sm:gap-3 justify-items-center ${className}`}>
            {Array.from({length}).map((_, idx) => (
                <input
                    key={idx}
                    ref={(el) => (refs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={value[idx] || ''}
                    onChange={(e) => handleChange(e.target.value, idx)}
                    onFocus={() => handleFocus(idx)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    onPaste={(e) => handlePaste(e, idx)}
                    className="w-full max-w-12 min-w-0 h-12 sm:h-14 text-center text-lg font-semibold text-gray-800 border border-gray-300 rounded-xl bg-transparent focus:border-gray-500 focus:outline-none"
                />
            ))}
        </div>
    )
}

export function formatOtpTimer(seconds) {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
}
